import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Heading from '@atlaskit/heading';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import EscalationPolicies from '@atlassian/eoc-escalation-policies';
import { useIsNewNavigation } from '@atlassian/eoc-global-context';
import RoutingRules from '@atlassian/eoc-routing-rules';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import Schedule, { ScheduleTours } from '@atlassian/eoc-schedule';
import { getSchedulesResource } from '@atlassian/eoc-schedule/resources';
import { useSpotlightActions } from '@atlassian/eoc-spotlight-store';

import { type PageProps } from '../../common/types';
import { getTeamResource } from '../../services/resources';

import { messages } from './messages';

const DEFAULT_HEIGHT_OFFSET = 80;

const oldContainerStyles = xcss({
	margin: '0 auto',
	maxWidth: '1280px',
	padding: 'space.300',
});

const containerStyles = xcss({
	padding: 'space.300',
});

const innerStyles = xcss({
	marginTop: 'space.400',
});

export const OnCallPage = ({ onReady, onRoutingRuleModalOpen }: PageProps) => {
	const intlApi = useIntl();
	const { data: team } = useResource(getTeamResource);
	const { data: getSchedulesData } = useResource(getSchedulesResource);
	const { trigger } = useSpotlightActions();

	const teamName = team?.values[0]?.name || '';
	const teamId = team?.values[0]?.atlassianIdentifier || '';
	const teamAvatar = team?.values[0]?.avatarUrl || undefined;

	useEffect(() => {
		if (onReady) {
			onReady();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		trigger(ScheduleTours.scheduleActions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const scrollToSchedule = useCallback(() => {
		const schedule = document.getElementById(location.hash.substring(1));

		if (schedule) {
			schedule.focus();
			window.scrollTo({
				behavior: 'auto',
				top:
					schedule.getBoundingClientRect().top -
					document.body.getBoundingClientRect().top -
					DEFAULT_HEIGHT_OFFSET,
			});
		}
	}, []);

	const isHorizontalNav = useIsNewNavigation();

	return (
		<Stack xcss={isHorizontalNav ? containerStyles : oldContainerStyles}>
			{!isHorizontalNav && (
				<>
					<Breadcrumbs>
						<BreadcrumbsItem
							text={intlApi.formatMessage(messages.teams)}
							href="/jira/people/search"
						/>
						<BreadcrumbsItem
							iconBefore={
								<Box paddingInlineEnd="space.050">
									<Avatar
										appearance="square"
										borderColor="transparent"
										size="xsmall"
										src={teamAvatar}
									/>
								</Box>
							}
							text={teamName}
							href={`/jira/people/team/${teamId}`}
						/>
					</Breadcrumbs>
					<Heading as="h1" size="large">
						{intlApi.formatMessage(messages.onCall)}
					</Heading>
				</>
			)}

			<Flex xcss={!isHorizontalNav && innerStyles} direction="column" rowGap="space.600">
				<RoutingRules
					onRoutingRuleModalOpen={onRoutingRuleModalOpen}
					schedules={getSchedulesData?.schedules || []}
				/>
				<EscalationPolicies />
				<Schedule onSchedulesReady={scrollToSchedule} />
			</Flex>
		</Stack>
	);
};
